// CustomerDetails.tsx (Refactored Main Component)
import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
} from "react";
import {
  Box,
  Typography,
  Skeleton,
  Paper,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button
} from "@mui/material";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { parsePhoneNumberFromString } from "libphonenumber-js/max";
import { format, isValid, parseISO } from "date-fns";

// Custom hooks and utilities
import { useApiClient } from "../useApiClient";
import { API_CONFIG, getApiUrl, NAVIGATION_CONFIG } from "../config/api";

// Types
import {
  CustomerDetailFormState,
  ValidationErrors,
  CareHome,
  CommunicationReason,
  CareGiver,
  CustomerPrecheckMatchResponse,
} from "../types/customer";
import { CustomerAddress } from "../types/address";

// Components
import { CustomerHeader } from "../components/CustomerHeader";
import { CustomerStatusBar } from "../components/CustomerStatusBar";
import { DuplicateToggleControls } from "../components/DuplicateToggleControls";
import { DuplicatePanel } from "../components/DuplicatePanel";
import { DuplicateDrawer } from "../components/DuplicateDrawer";
import { CustomerDetailsForm } from "../components/CustomerDetailsForm";
import { AddressesSection } from "../components/AddressesSection";
import { SharedSpacePanel } from "../components/SharedSpacePanel";
import { CareHomeSelectionModal } from "../components/CareHomeSelectionModal";
import { CommunicationPreferences } from "../components/CommunicationPreferences";
import { ActionButtons } from "../components/ActionButtons";
import { spacing } from "../theme";
import { NextOfKinSection } from "../components/NextOfKinSection";
import { SystemType } from "../components/DesktopAppMenu";
import { SyncStatus } from "../components/SyncStatusMenu";

// Dialogs
import { CommunicationPreferencesDialog } from "../components/dialogs/CommunicationPreferencesDialog";
import { DuplicateWarningDialog } from "../components/dialogs/DuplicateWarningDialog";
import { MarkAsDeceasedDialog } from "../components/dialogs/MarkAsDeceasedDialog";
import { DeleteCustomerDialog } from "../components/dialogs/DeleteCustomerDialog";
import { NoEmailConfirmationDialog } from "../components/dialogs/NoEmailConfirmationDialog";

// Modals
import { AddressLookupModal } from "../components/AddressLookupModal";
import { AddressModal } from "../components/AddressModal";

export default function CustomerDetails() {
  // URL params and navigation
  const { id } = useParams();
  const isNew = id === "new";
  const navigate = useNavigate();
  const location = useLocation();
  const { fromPage, pageSize } = location.state || {
    fromPage: 0,
    pageSize: 10,
  };

  // API client
  const { callApi } = useApiClient();

  // Loading and error states
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<ValidationErrors>(
    {}
  );
  const [customerToken, setCustomerToken] = useState<string | null>(null);

  // Form data and state
  const defaultCustomer = useMemo<CustomerDetailFormState>(
    () => ({
      firstName: "",
      lastName: "",
      dateOfBirth: "",
      email: "",
      phones: [],
      addresses: [],
      isActive: true,
      title: "",
      patientGenderCode: "X",
      isDeceased: false,
      deceasedDate: null,
      isCommercialClient: true,
      careHomeExternalId: undefined,
      communicationPreference: {
        allowEmail: null,
        allowSms: null,
        allowPhone: null,
        allowLetter: null,
        reason: 1,
      },
      hasMatch: false,
    }),
    []
  );

  const [formData, setFormData] =
    useState<CustomerDetailFormState>(defaultCustomer);
  const [formDataLoaded, setFormDataLoaded] = useState(false);

  // Date picker states
  const [dobInputTempValue, setDobInputTempValue] = useState<Date | null>(null);
  const [deceasedInputTempValue, setDeceasedInputTempValue] =
    useState<Date | null>(null);

  // Form field states
  const [hasNoEmailChecked, setHasNoEmailChecked] = useState(false);
  const [hasNoPhoneChecked, setHasNoPhoneChecked] = useState(false);
  const [hasInitialized, setHasInitialized] = useState(false);
  const [phoneValidationErrors, setPhoneValidationErrors] = useState<
    (string | undefined)[]
  >([]);

  // Care homes
  const [careHomes, setCareHomes] = useState<CareHome[]>([]);
  const [loadingCareHomes, setLoadingCareHomes] = useState(false);
  const [careHomeSearch, setCareHomeSearch] = useState("");
  const careHomePage = 1;
  const careHomePageSize = 50;

  // Address modals
  const [isAddressLookupModalOpen, setIsAddressLookupModalOpen] =
    useState(false);
  const [isAddressModalOpen, setIsAddressModalOpen] = useState(false);
  const [selectedAddress, setSelectedAddress] = useState<
    CustomerAddress | undefined
  >(undefined);

  // Care home selection modal state  
  const [isCareHomeModalOpen, setIsCareHomeModalOpen] = useState(false);

  // Duplicate detection
  const [duplicateMatches, setDuplicateMatches] = useState<any[]>([]);
  const [checkingDuplicates, setCheckingDuplicates] = useState(false);
  const [hasMatch, setHasMatch] = useState(false);
  const [showDuplicates, setShowDuplicates] = useState(isNew);
  const [showSharedSpace, setShowSharedSpace] = useState(false);
  const [isSharedSpaceManuallyChanged, setIsSharedSpaceManuallyChanged] = useState(false);
  const [sharedSpaceToggleDialogOpen, setSharedSpaceToggleDialogOpen] = useState(false);
  const [duplicateDisplayMode, setDuplicateDisplayMode] = useState<
    "panel" | "drawer"
  >("panel");
  const [drawerMinimized, setDrawerMinimized] = useState(false);
  const [confirmedNotDuplicate, setConfirmedNotDuplicate] = useState(false);
  const [proceedAnyway, setProceedAnyway] = useState(false);
  const [duplicateCustomerMatchingThreshold, setDuplicateCustomerMatchingThreshold] = useState(70);
  const debounceTimer = useRef<NodeJS.Timeout | null>(null);
  const drawerWidth = 400;

  // Communication preferences warning states
  const [allNull, setAllNull] = useState(false);
  const [someNull, setSomeNull] = useState(false);

  // Dialog states
  const [showCommPrefDialog, setShowCommPrefDialog] = useState(false);
  const [showDuplicateWarningDialog, setShowDuplicateWarningDialog] =
    useState(false);
  const [showMarkAsDeceasedWarningDialog, setShowMarkedAsDeceasedDialog] =
    useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [showNoEmailDialog, setShowNoEmailDialog] = useState(false);

  // Toast state
  const [toastOpen, setToastOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [toastSeverity, setToastSeverity] = useState<"success" | "error">("success");

  // Toast helper method
  const showToast = (message: string, severity: "success" | "error" = "success") => {
    setToastMessage(message);
    setToastSeverity(severity);
    setToastOpen(true);
  };

  // Reusable function to handle care home preselection
  const handleCareHomePreselection = useCallback(async (careHomeExternalId: string, replaceAddresses: boolean = false) => {
    try {
      // Fetch care home details
      const careHomeResult = await callApi(
        getApiUrl(API_CONFIG.ENDPOINTS.CAREHOMES, careHomeExternalId)
      );

      // Find the primary address from the care home
      const careHomeAddresses = careHomeResult.data?.addresses || [];
      const primaryCareHomeAddress = careHomeAddresses.find((addr: any) => addr.isPrimary)
        || careHomeAddresses[0]; // fallback to first address if no primary found

      let careHomeCustomerAddress: any = null;
      if (primaryCareHomeAddress) {
        // Create a customer address from the care home address
        careHomeCustomerAddress = {
          externalId: primaryCareHomeAddress.externalId,
          isActive: true,
          isPrimary: true, // Make care home address the primary address
          fromCareHome: true, // Mark as care home address
          addressType: 'Care Home',
          nameNumber: primaryCareHomeAddress.nameNumber || '',
          addressShortName: primaryCareHomeAddress.addressShortName || '',
          addressName: primaryCareHomeAddress.addressName || careHomeResult.data?.name || '',
          street: primaryCareHomeAddress.street || '',
          town: primaryCareHomeAddress.town || '',
          postcode: primaryCareHomeAddress.postcode || '',
          organisation: primaryCareHomeAddress.organisation || '',
          department: primaryCareHomeAddress.department || '',
          district: primaryCareHomeAddress.district || '',
          county: primaryCareHomeAddress.county || '',
          country: primaryCareHomeAddress.country || 'UK',
        };
      }

      // Update form data to include the pre-selected care home and its address
      setFormData(prev => ({
        ...prev,
        careHomeExternalId: careHomeExternalId,
        addresses: careHomeCustomerAddress
          ? replaceAddresses
            ? [careHomeCustomerAddress] // Replace existing addresses (for new customer)
            : [...(prev.addresses || []), careHomeCustomerAddress] // Append to existing addresses (for existing customer)
          : replaceAddresses
            ? []
            : prev.addresses,
      }));

      // Add the care home to the care homes list if not already present
      setCareHomes(prevCareHomes => {
        const exists = prevCareHomes.some(ch => ch.externalId === careHomeExternalId);
        if (!exists) {
          return [careHomeResult.data, ...prevCareHomes];
        }
        return prevCareHomes;
      });

    } catch (err) {
      console.error('Failed to preselect care home:', err);
    }
  }, [callApi]);

  // Initialize form data from URL token
  useEffect(() => {
    const fetchCustomer = async () => {
      const params = new URLSearchParams(window.location.search);
      const token = params.get("token");
      if (!token) return;

      setCustomerToken(token);

      // If we're not on the 'new' page, just set the token for association
      // Don't prefill data for existing customers
      if (!isNew) return;

      try {
        const response = await callApi(
          `${API_CONFIG.BASE_URL
          }${API_CONFIG.ENDPOINTS.CUSTOMER_LINK_BY_TOKEN.replace(
            "{token}",
            token
          )}`
        );

        const data = response.data.customerData as CustomerDetailFormState;
        const linkedToSchemeId = response.data.customerData.linkedToSchemeId;

        const phonesWithType = (data.phones || []).map((phone) => {
          let detectedType: string = "Unknown";
          if (phone.number) {
            const parsedPhone = parsePhoneNumberFromString(phone.number, "GB");
            if (parsedPhone && parsedPhone.isValid()) {
              detectedType = parsedPhone.getType() || "Unknown";
            }
          }
          return { ...phone, type: detectedType };
        });

        // Set initial form data
        setFormData({
          ...defaultCustomer,
          ...data,
          phones: phonesWithType,
          addresses: data.addresses || [],
        });

        // Handle linkedToSchemeId if present
        if (linkedToSchemeId) {
          try {
            // Check conversion status
            const conversionResponse = await callApi(
              `${getApiUrl(API_CONFIG.ENDPOINTS.CHECK_CONVERSION_STATUS)}/${linkedToSchemeId}`,
              { method: "GET" }
            );

            const conversionNeeded = conversionResponse.data?.conversionNeeded;

            if (conversionNeeded === false) {
              // No conversion needed - extract care home and set it up
              const careHomeExternalId = conversionResponse.data?.careHomeExternalId;

              if (careHomeExternalId) {
                await handleCareHomePreselection(careHomeExternalId, false);
              }
            } else if (conversionNeeded === true) {
              // Conversion needed - perform address conversion
              const customerExternalId = conversionResponse.data?.customerExternalId;
              const locationExternalId = conversionResponse.data?.locationExternalId;

              if (customerExternalId && locationExternalId) {
                await handleAddressConversion(customerExternalId, locationExternalId);
              }
            }
          } catch (conversionErr) {
            console.error("Failed to check conversion status:", conversionErr);
            // Don't show error to user as this is not critical for basic customer loading
          }
        }
      } catch (err: any) {
        if (err.response?.status === 404) {
          navigate(NAVIGATION_CONFIG.ROUTES.CUSTOMERS_NEW);
        }
        console.error("Invalid data received", err);
      }
    };

    const handleAddressConversion = async (customerExternalId: string, locationExternalId: string) => {
      try {
        // Make API call to convert address to shared space
        const response = await callApi(
          getApiUrl(`${API_CONFIG.ENDPOINTS.CUSTOMERS}/${customerExternalId}/convertspace/${locationExternalId}`),
          { method: "POST" }
        );

        // Extract the shared space ID from response
        const sharedSpaceId = response?.data?.careHomeExternalId;

        if (sharedSpaceId) {
          // Load and select the care home with its address - append to existing addresses
          await handleCareHomePreselection(sharedSpaceId, false);
        }
      } catch (err) {
        console.error('Failed to convert address to shared space:', err);
      }
    };

    fetchCustomer();
  }, [callApi, defaultCustomer, navigate, handleCareHomePreselection, isNew]);

  // Fetch customer details for existing customers
  const fetchCustomerDetails = useCallback(async () => {
    if (isNew) {
      // Reset form data to default for new customers
      setFormData(defaultCustomer);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const result = await callApi(
        getApiUrl(API_CONFIG.ENDPOINTS.CUSTOMERS, id)
      );
      setFormData(result.data);
    } catch (err: any) {
      console.error("Failed to fetch customer details:", err);
      setError(err.message || "Failed to load customer details");
    } finally {
      setLoading(false);
    }
  }, [isNew, callApi, id, defaultCustomer]);

  useEffect(() => {
    fetchCustomerDetails().then(() => setFormDataLoaded(true));
  }, [id, fetchCustomerDetails]);

  // Initialize email and phone check states
  useEffect(() => {
    if (formData.phones.length === 0 && !isNew) {
      setHasNoPhoneChecked(true);
    }
  }, [formData.phones, isNew]);

  useEffect(() => {
    if (!hasInitialized && formDataLoaded) {
      const shouldBeChecked =
        (!formData.email || formData.email.trim() === "") && !isNew;
      setHasNoEmailChecked(shouldBeChecked);
      setHasInitialized(true);
    }
  }, [formData.email, isNew, hasInitialized, formDataLoaded]);

  // Update date picker values when form data changes
  useEffect(() => {
    if (formData.dateOfBirth) {
      const parsed = parseISO(formData.dateOfBirth);
      setDobInputTempValue(isValid(parsed) ? parsed : null);
    } else {
      setDobInputTempValue(null);
    }
  }, [formData.dateOfBirth]);

  // Handle sharedspace URL parameter for new customers
  useEffect(() => {
    if (isNew && formDataLoaded) {
      const urlParams = new URLSearchParams(window.location.search);
      const sharedSpaceId = urlParams.get('sharedspace');

      if (sharedSpaceId && sharedSpaceId !== formData.careHomeExternalId) {
        // Use reusable function to handle care home preselection
        // For new customers, replace all addresses with just the care home address
        const fetchSharedSpace = async () => {
          try {
            await handleCareHomePreselection(sharedSpaceId, true);
          } catch (err) {
            console.error('Failed to fetch shared space for pre-selection:', err);
            // If fetch fails, still set the ID so it can be selected manually
            setFormData(prev => ({
              ...prev,
              careHomeExternalId: sharedSpaceId
            }));
          }
        };

        fetchSharedSpace();
      }
    }
  }, [isNew, formDataLoaded, formData.careHomeExternalId, callApi, handleCareHomePreselection]);

  // Update shared space toggle based on careHomeExternalId (only if not manually changed)
  useEffect(() => {
    if (!isSharedSpaceManuallyChanged) {
      setShowSharedSpace(!!formData.careHomeExternalId);
    }
  }, [formData.careHomeExternalId, isSharedSpaceManuallyChanged]);

  // Handle shared space toggle change
  const handleSharedSpaceToggleChange = useCallback((checked: boolean) => {
    setIsSharedSpaceManuallyChanged(true);

    if (!checked && showSharedSpace) {
      // Show confirmation dialog when turning OFF shared space
      setSharedSpaceToggleDialogOpen(true);
    } else {
      // Handle turning ON shared space immediately
      setShowSharedSpace(checked);
      // When turning on shared space, remove primary flag from all non-care home addresses
      setFormData((prev) => ({
        ...prev,
        addresses: prev.addresses.map(address => ({
          ...address,
          isPrimary: address.fromCareHome ? address.isPrimary : false
        }))
      }));
    }
  }, [showSharedSpace]);

  // Handle confirmation of shared space toggle off
  const handleConfirmSharedSpaceToggleOff = useCallback(() => {
    setShowSharedSpace(false);
    // When turning off shared space, clear careHomeExternalId and remove care home addresses
    setFormData((prev) => ({
      ...prev,
      careHomeExternalId: undefined,
      addresses: prev.addresses.filter(address => !address.fromCareHome)
    }));
    setSharedSpaceToggleDialogOpen(false);
  }, []);

  // Handle canceling shared space toggle off
  const handleCancelSharedSpaceToggleOff = useCallback(() => {
    setSharedSpaceToggleDialogOpen(false);
  }, []);

  // Handle opening the care home selection modal
  const handleAddCareHome = useCallback(() => {
    setIsCareHomeModalOpen(true);
  }, []);

  // Handle care home selection
  const handleSelectCareHome = useCallback(async (careHome: any) => {
    try {
      // If we have primaryAddress from search, we can add it directly
      if (careHome.primaryAddress) {
        const careHomeCustomerAddress = {
          externalId: careHome.primaryAddress.externalId || '',
          isActive: careHome.primaryAddress.isActive,
          isPrimary: careHome.primaryAddress.isPrimary,
          fromCareHome: true, // Mark as care home address
          title: careHome.primaryAddress.title || '',
          nameNumber: careHome.primaryAddress.nameNumber || '',
          addressShortName: careHome.primaryAddress.addressShortName || '',
          addressName: careHome.primaryAddress.addressName || careHome.name || '',
          street: careHome.primaryAddress.street || '',
          town: careHome.primaryAddress.town || '',
          postcode: careHome.primaryAddress.postcode || '',
          organisation: careHome.primaryAddress.organisation || '',
          department: careHome.primaryAddress.department || '',
          district: careHome.primaryAddress.district || '',
          county: careHome.primaryAddress.county || '',
          country: careHome.primaryAddress.country || 'UK',
        };

        setFormData((prev) => ({
          ...prev,
          careHomeExternalId: careHome.externalId,
          addresses: [
            ...prev.addresses.filter(addr => !addr.fromCareHome), // Remove existing care home addresses
            careHomeCustomerAddress // Add the new care home address
          ]
        }));
      } else {
        // Fall back to the existing handleCareHomePreselection for fetching full details
        // Remove existing care home addresses first
        setFormData((prev) => ({
          ...prev,
          addresses: prev.addresses.filter(addr => !addr.fromCareHome)
        }));
        await handleCareHomePreselection(careHome.externalId, false);
      }

      setIsCareHomeModalOpen(false);
    } catch (error) {
      console.error('Error adding care home:', error);
    }
  }, [handleCareHomePreselection]);

  // Search care homes function
  const handleSearchCareHomes = useCallback(async (searchTerm: string) => {
    try {
      // Use the care homes search endpoint with query parameter
      const result = await callApi(
        `${getApiUrl(API_CONFIG.ENDPOINTS.CAREHOMES)}/search?searchTerm=${encodeURIComponent(searchTerm)}`
      );
      return result.data || [];
    } catch (error) {
      console.error('Error searching care homes:', error);
      return [];
    }
  }, [callApi]);

  useEffect(() => {
    if (formData.deceasedDate) {
      const parsed = parseISO(formData.deceasedDate);
      setDeceasedInputTempValue(isValid(parsed) ? parsed : null);
    } else {
      setDeceasedInputTempValue(null);
    }
  }, [formData.deceasedDate]);

  // Update communication preferences warning state
  useEffect(() => {
    if (!formData || !formData.communicationPreference) return;
    const commPrefs = formData.communicationPreference;
    const commPrefsValues = [
      commPrefs.allowEmail,
      commPrefs.allowSms,
      commPrefs.allowPhone,
      commPrefs.allowLetter,
    ];
    const nullCount = commPrefsValues.filter((v) => v === null).length;
    setAllNull(nullCount === commPrefsValues.length);
    setSomeNull(nullCount > 0 && nullCount < commPrefsValues.length);
  }, [formData]);


  const fetchCareHomes = useCallback(
    async (search: string = "", page: number = 1) => {
      try {
        setLoadingCareHomes(true);
        const params = new URLSearchParams({
          page: String(page),
          pageSize: String(careHomePageSize),
        });
        if (search) {
          params.append("filterBy", "name");
          params.append("filterValue", encodeURIComponent(search));
        }
        const response = await callApi(
          `${getApiUrl(API_CONFIG.ENDPOINTS.CAREHOMES)}?${params.toString()}`,
          { method: "GET" }
        );

        let careHomesData: CareHome[] = Array.isArray(response.data) ? response.data : response.data?.items || [];

        // If we have a customer with a care home that's not in the current list, fetch and prepend it
        if (formData.careHomeExternalId && formDataLoaded) {
          const customerCareHomeExists = careHomesData.some(
            (ch: CareHome) => ch.externalId === formData.careHomeExternalId
          );

          if (!customerCareHomeExists) {
            try {
              const careHomeResult = await callApi(
                getApiUrl(API_CONFIG.ENDPOINTS.CAREHOMES, formData.careHomeExternalId)
              );
              careHomesData = [careHomeResult.data, ...careHomesData];
            } catch (careHomeErr) {
              console.error("Failed to fetch customer's care home:", careHomeErr);
            }
          }
        }

        setCareHomes(careHomesData);
      } catch (err) {
        console.error("Failed to fetch care homes:", err);
        setError("Failed to load shared addresses");
      } finally {
        setLoadingCareHomes(false);
      }
    },
    [callApi, formData.careHomeExternalId, formDataLoaded]
  );

  useEffect(() => {
    fetchCareHomes(careHomeSearch, careHomePage);
  }, [careHomeSearch, careHomePage, fetchCareHomes]);

  // Duplicate detection setup
  useEffect(() => {
    setDuplicateDisplayMode("drawer");
    setDrawerMinimized(false);
  }, []);

  // Real-time duplicate check
  useEffect(() => {
    if (!showDuplicates) return;

    const { firstName, lastName, dateOfBirth, email, phones, addresses } =
      formData;
    const primaryPhone =
      phones.find((p) => p.isPrimary)?.number || phones[0]?.number || "";
    const primaryPostcode =
      addresses.find((a) => a.isPrimary)?.postcode ||
      addresses[0]?.postcode ||
      "";

    if (
      !firstName.trim() &&
      !lastName.trim() &&
      !dateOfBirth &&
      !email.trim() &&
      !primaryPhone.trim()
    ) {
      setDuplicateMatches([]);
      return;
    }

    if (debounceTimer.current) clearTimeout(debounceTimer.current);

    const abortController = new AbortController();

    debounceTimer.current = setTimeout(async () => {
      setCheckingDuplicates(true);
      try {
        const payload = {
          externalId: isNew ? null : formData.externalId || id || null,
          firstName: firstName.trim(),
          lastName: lastName.trim(),
          dateOfBirth: dateOfBirth ? dateOfBirth : null,
          email: email ? email.trim() : null,
          primaryPhone: primaryPhone.trim(),
          primaryPostcode: primaryPostcode.trim()
        };

        const result = await callApi(
          `${getApiUrl(API_CONFIG.ENDPOINTS.CUSTOMERS_PRECHECK)}`,
          {
            method: "POST",
            data: payload,
            signal: abortController.signal
          }
        );

        const response = result.data as CustomerPrecheckMatchResponse;
        const matches = Array.isArray(response?.customers)
          ? response.customers
          : [];

        setDuplicateCustomerMatchingThreshold(response.duplicateCustomerMatchingThreshold);
        setHasMatch(matches.some((item) => item.isMatch === true));
        setDuplicateMatches(matches);

      } catch (err: any) {
        // Don't update state if request was aborted
        if (err.name === 'CanceledError' || err.code === 'ERR_CANCELED') {
          console.log('Duplicate check request was cancelled');
          return;
        }
        setDuplicateMatches([]);
      } finally {
        // Only update if not aborted
        if (!abortController.signal.aborted) {
          setCheckingDuplicates(false);
        }
      }
    }, 600);

    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
      abortController.abort();
    };
  }, [formData, showDuplicates, callApi, id, isNew]);

  // Event handlers
  const handleBack = () => {
    navigate(NAVIGATION_CONFIG.ROUTES.CUSTOMERS, {
      state: {
        returnedFromDetails: true,
        page: fromPage,
        pageSize: pageSize,
      },
    });
  };

  const handleInputChange = (e: any) => {
    const target = e.target;
    const name = target.name;
    const isCheckbox = target.type === "checkbox";
    const value = isCheckbox
      ? !!(target as { checked?: boolean }).checked
      : target.value;

    if (!name) return;

    if (name === "isDeceased" && value === true) {
      setShowMarkedAsDeceasedDialog(true);
    } else if (name === "isDeceased" && value === false) {
      setDeceasedInputTempValue(null);
      setFormData((prev) => ({
        ...prev,
        isDeceased: false,
        deceasedDate: null,
      }));
    } else if (name.startsWith("communicationPreference.")) {
      const [, field] = name.split(".");
      let parsedValue: boolean | null = null;
      if (value === "true" || value === true) parsedValue = true;
      else if (value === "false" || value === false) parsedValue = false;
      else parsedValue = null;
      setFormData((prev) => ({
        ...prev,
        communicationPreference: {
          ...prev.communicationPreference,
          [field]: parsedValue,
        },
      }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }

    if (validationErrors[name]) {
      setValidationErrors((prev) => ({ ...prev, [name]: undefined }));
    }

    if (error) {
      setError(null);
    }
  };

  const handleContinueSetMarkedAsDeceased = () => {
    setShowMarkedAsDeceasedDialog(false);
    setFormData((prev) => ({
      ...prev,
      isDeceased: true,
      communicationPreference: {
        ...prev.communicationPreference,
        allowEmail: false,
        allowSms: false,
        allowPhone: false,
        allowLetter: false,
        reason: CommunicationReason.MarkedAsDeceased,
      },
    }));
    setValidationErrors((prev) => ({ ...prev, email: "", address: "" }));
  };

  const handleHasNoEmailCheckedChanged = (event: any) => {
    const isChecked = event.target.checked;

    if (isChecked) {
      // Show confirmation dialog before checking the box
      setShowNoEmailDialog(true);
    } else {
      // Unchecking the box - proceed normally
      setHasNoEmailChecked(false);
      if (
        (!formData.email || formData.email.trim() === "") &&
        !formData.isDeceased
      ) {
        setValidationErrors((prev) => ({
          ...prev,
          email:
            "Email is required. If the customer has no email address, please check the 'No Email' option.",
        }));
      }
    }
  };

  const handleNoEmailDialogContinue = () => {
    // User confirmed - check the box and clear email
    setHasNoEmailChecked(true);
    handleInputChange({ target: { name: "email", value: "" } });
    setValidationErrors((prev) => ({ ...prev, email: "" }));
    setShowNoEmailDialog(false);
  };

  const handleNoEmailDialogClose = () => {
    // User cancelled - don't check the box
    setShowNoEmailDialog(false);
  };

  // Handle customer selection from duplicates when token is present
  const handleSelectCustomer = async (selectedCustomer: any) => {
    try {
      setLoading(true);

      // Fetch full customer details
      const result = await callApi(
        getApiUrl(API_CONFIG.ENDPOINTS.CUSTOMERS, selectedCustomer.customerExternalId)
      );

      // Load the selected customer data
      setFormData(result.data);

      // Update the URL to the selected customer while preserving the token
      const params = new URLSearchParams(window.location.search);
      const token = params.get("token");
      const newUrl = NAVIGATION_CONFIG.getCustomerDetailRoute(selectedCustomer.customerExternalId);

      if (token) {
        navigate(`${newUrl}?token=${token}`, { replace: true });
      } else {
        navigate(newUrl, { replace: true });
      }

      // Hide duplicates panel since customer is now selected
      setShowDuplicates(false);
      setHasMatch(false);

      showToast("Customer selected successfully");
    } catch (err: any) {
      console.error("Failed to load selected customer:", err);
      setError(err.message || "Failed to load selected customer");
    } finally {
      setLoading(false);
    }
  };

  // Desktop app handler
  const handleOpenDesktopApp = async (selectedSystem: SystemType) => {
    if (!formData.externalId) return;
    try {
      const response = await callApi(
        getApiUrl(API_CONFIG.ENDPOINTS.RESOLVE_LEGACY_IDS, formData.externalId)
      );
      const opticsSchemeId = response?.data?.opticsSchemeId; // system is called 'optics'
      const hearingId = response?.data?.hearingPatientId;

      if (selectedSystem === 'hearing') {
        if (hearingId) {
          const encoded = encodeURIComponent(String(hearingId).trim());
          window.location.href = `scproto://hearing/${encoded}`;
        } else {
          alert("No hearingId found for this customer.");
        }
      } else if (selectedSystem === 'optics') {
        if (opticsSchemeId) {
          const encoded = encodeURIComponent(String(opticsSchemeId).trim());
          window.location.href = `scproto://optics/${encoded}`;
        } else {
          alert("No opticsSchemeId found for this customer.");
        }
      }
    } catch (err: any) {
      if (err.response?.status === 404) {
        alert("No legacy mapping found for this customer.");
      } else {
        alert(
          "Failed to launch desktop app. Please refresh the page and try again."
        );
      }
    }
  };

  // Sync status handlers
  const handleCheckSyncStatus = async (): Promise<SyncStatus> => {
    if (!formData.externalId) {
      return { hearing: false, optics: false };
    }

    try {
      const response = await callApi(
        getApiUrl(API_CONFIG.ENDPOINTS.RESOLVE_LEGACY_IDS, formData.externalId)
      );

      const hearingId = response?.data?.hearingPatientId;
      const opticsSchemeId = response?.data?.opticsSchemeId;
      const customerStageId = response?.data?.customerStageId;

      return {
        hearing: !!hearingId, // true if hearingId exists, false otherwise
        optics: !!(opticsSchemeId || customerStageId) // true if either exists, false otherwise
      };
    } catch (err: any) {
      console.error('Failed to check sync status:', err);
      return { hearing: false, optics: false };
    }
  };

  const handleForceSync = async (system: 'hearing' | 'optics'): Promise<void> => {
    if (!formData.externalId) {
      throw new Error('No customer external ID found');
    }

    try {
      await callApi(
        `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.FORCE_SYNC.replace("{id}", formData.externalId)}`,

        {
          method: 'POST',
          data: {
            targetSystem: system
          }
        }
      );
    } catch (err: any) {
      console.error(`Failed to force sync ${system}:`, err);
      throw err;
    }
  };

  // Date handlers
  const handleDobAccept = (newValue: Date | null) => {
    if (newValue instanceof Date && isValid(newValue)) {
      handleInputChange({
        target: { name: "dateOfBirth", value: format(newValue, "yyyy-MM-dd") },
      });
    }
  };

  const handleDeceasedDateAccept = (newValue: Date | null) => {
    if (newValue instanceof Date && isValid(newValue)) {
      handleInputChange({
        target: { name: "deceasedDate", value: format(newValue, "yyyy-MM-dd") },
      });
    }
  };

  // Care home handlers
  const handleCareHomeChange = async (newValue: CareHome | null) => {
    const prevCareHomeId = formData.careHomeExternalId;
    const newCareHomeId = newValue?.externalId;

    // First update the form data to remove old care home addresses
    setFormData((prev) => {
      let updatedAddresses = [...(prev.addresses || [])];

      // Scenario 1: Moving out of a care home (had care home, now doesn't)
      // Scenario 3: Moving between care homes (had one, now has different one)
      if (prevCareHomeId && (!newCareHomeId || prevCareHomeId !== newCareHomeId)) {
        // Remove any care home addresses
        updatedAddresses = updatedAddresses.filter(address => !address.fromCareHome);
      }

      return {
        ...prev,
        careHomeExternalId: newCareHomeId,
        addresses: updatedAddresses,
      };
    });

    // Scenario 2 & 3: Moving into a care home or between care homes
    // Fetch care home details and add its primary address
    if (newCareHomeId) {
      try {
        const careHomeDetails = await callApi(
          getApiUrl(API_CONFIG.ENDPOINTS.CAREHOMES, newCareHomeId)
        );

        // Find the primary address from the care home
        const careHomeAddresses = careHomeDetails.data?.addresses || [];
        const primaryCareHomeAddress = careHomeAddresses.find((addr: any) => addr.isPrimary)
          || careHomeAddresses[0]; // fallback to first address if no primary found

        if (primaryCareHomeAddress) {
          // Create a customer address from the care home address
          const careHomeCustomerAddress: CustomerAddress = {
            externalId: primaryCareHomeAddress.externalId,
            isActive: true,
            isPrimary: true, // Make care home address the primary address
            fromCareHome: true, // Mark as care home address
            addressType: 'Care Home',
            nameNumber: primaryCareHomeAddress.nameNumber || '',
            addressShortName: primaryCareHomeAddress.addressShortName || '',
            addressName: primaryCareHomeAddress.addressName || newValue?.name || '',
            street: primaryCareHomeAddress.street || '',
            town: primaryCareHomeAddress.town || '',
            postcode: primaryCareHomeAddress.postcode || '',
            organisation: primaryCareHomeAddress.organisation || '',
            department: primaryCareHomeAddress.department || '',
            district: primaryCareHomeAddress.district || '',
            county: primaryCareHomeAddress.county || '',
            country: primaryCareHomeAddress.country || 'UK',
          };

          // Add the care home address and make sure no other address is primary
          setFormData((prev) => {
            const updatedAddresses = prev.addresses.map(addr => ({
              ...addr,
              isPrimary: false // Remove primary flag from existing addresses
            }));

            return {
              ...prev,
              addresses: [...updatedAddresses, careHomeCustomerAddress],
            };
          });
        }
      } catch (err) {
        console.error("Failed to fetch care home details:", err);
        // Don't show error to user as this is not critical
      }
    }

    if (validationErrors.careHomeExternalId) {
      setValidationErrors((prev) => ({
        ...prev,
        careHomeExternalId: undefined,
      }));
    }
  };

  const handleCareHomeInputChange = (value: string) => {
    setCareHomeSearch(value);
  };

  // Computed value to determine if address panel should be disabled
  const isAddressPanelDisabled = Boolean(formData.careHomeExternalId) || formData.isDeceased;

  // Communication preferences reason handler
  const handleCommunicationReasonChange = (reason: CommunicationReason) => {
    setFormData((prev) => ({
      ...prev,
      communicationPreference: {
        ...prev.communicationPreference,
        reason: reason,
      },
    }));
  };

  // Address handlers
  const handleAddAddress = () => {
    setIsAddressLookupModalOpen(true);
  };

  const handleAddressSelect = (address: CustomerAddress) => {
    setFormData((prev) => ({
      ...prev,
      addresses: [...prev.addresses, address],
    }));
    setIsAddressLookupModalOpen(false);
  };

  const handleRemoveAddress = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      addresses: prev.addresses.filter((_, i) => i !== index),
    }));
  };

  const handleEditAddress = (address: CustomerAddress) => {
    setSelectedAddress(address);
    setIsAddressModalOpen(true);
  };

  const handleCareHomeClick = () => {
    if (formData.careHomeExternalId) {
      const careHomeUrl = `${window.location.origin}${NAVIGATION_CONFIG.getCareHomeDetailRoute(formData.careHomeExternalId)}`;
      window.open(careHomeUrl, "_blank", "noopener,noreferrer");
    }
  };

  const handleConvertToSharedSpace = async (address: CustomerAddress) => {
    // Check if customer has been saved (has an ID)
    if (isNew || !id) {
      showToast("Save customer before continue", "error");
      return;
    }

    // Check if address has an external ID =>> this will never happen as we generate id-s for the addresses when creating them
    if (!address.externalId) {
      showToast("Save customer before continue", "error");
      return;
    }

    try {
      setLoading(true);

      // Make API call to convert address to shared space
      const response = await callApi(
        getApiUrl(`${API_CONFIG.ENDPOINTS.CUSTOMERS}/${id}/convertspace/${address.externalId}`),
        { method: "POST" }
      );

      // Extract the shared space ID from response
      const sharedSpaceId = response?.data?.careHomeExternalId;

      if (sharedSpaceId) {
        // Navigate to new customer page with shared space parameter
        navigate(`${NAVIGATION_CONFIG.getCustomerDetailRoute('new')}?sharedspace=${sharedSpaceId}`);
      } else {
        throw new Error("No shared space ID returned from conversion");
      }

    } catch (err: any) {
      console.error("Failed to convert address to shared space:", err);
      showToast("Failed to convert address to shared address", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleAddNewAddress = () => {
    setIsAddressLookupModalOpen(false);
    setIsAddressModalOpen(true);
  };

  const handleSaveAddress = (address: CustomerAddress) => {
    setFormData((prev) => {
      const index = prev.addresses.findIndex(
        (a) => a.externalId === address.externalId
      );
      if (index === -1) {
        return { ...prev, addresses: [...prev.addresses, address] };
      }
      const updatedAddresses = [...prev.addresses];
      updatedAddresses[index] = address;
      return { ...prev, addresses: updatedAddresses };
    });
    setSelectedAddress(undefined);
    setIsAddressModalOpen(false);
  };

  const handleUpdateAddress = (
    index: number,
    updates: Partial<CustomerAddress>
  ) => {
    setFormData((prev) => {
      const newAddresses = [...prev.addresses];
      if (updates.isPrimary) {
        newAddresses.forEach((addr) => {
          addr.isPrimary = false;
        });
      }
      newAddresses[index] = { ...newAddresses[index], ...updates };
      return { ...prev, addresses: newAddresses };
    });
  };

  // Save and submit handlers
  const saveCustomer = async () => {
    try {
      setLoading(true);
      setError(null);
      setValidationErrors({});

      if (isNew) {
        const response = await callApi(
          getApiUrl(API_CONFIG.ENDPOINTS.CUSTOMERS),
          {
            method: "POST",
            data: formData,
          }
        );
        const newExternalId = response?.data?.externalId;
        if (newExternalId) {
          // If the customer was created using a token, update the customer link
          if (customerToken) {
            try {
              await callApi(
                `${API_CONFIG.BASE_URL
                }${API_CONFIG.ENDPOINTS.CUSTOMER_LINK_BY_TOKEN.replace(
                  "{token}",
                  customerToken
                )}`,
                {
                  method: "PUT",
                  data: { customerExternalId: newExternalId },
                }
              );
            } catch (err) {
              console.error("Failed to update customer link:", err);
            }
          }

          showToast("Customer created successfully");

          navigate(NAVIGATION_CONFIG.getCustomerDetailRoute(newExternalId), {
            replace: true,
          });

          // Remove token from URL after successful association
          setCustomerToken(null);
          return;
        }
      } else {
        const response = await callApi(
          getApiUrl(API_CONFIG.ENDPOINTS.CUSTOMERS, id),
          {
            method: "PUT",
            data: formData,
          }
        );
        setFormData(response.data);
        showToast("Customer updated successfully");
      }
    } catch (err: any) {
      if (err.response?.status === 400 && err.response?.data?.errors) {
        const errorMap: ValidationErrors = {};
        err.response.data.errors.forEach(
          (error: { field: string; error: string }) => {
            // Keep the original field name for caregiver errors
            const caregiverMatch = error.field.match(
              /^CareGivers\[(\d+)\]\.(.+)$/
            );
            if (caregiverMatch) {
              errorMap[error.field] = error.error;
            } else {
              let fieldName =
                error.field.charAt(0).toLowerCase() + error.field.slice(1);
              if (error.field.toLowerCase() === "reason") {
                fieldName = "communicationPreference.reason";
              }
              errorMap[fieldName] = error.error;
            }
          }
        );

        setValidationErrors(errorMap);
        setError("Please correct the validation errors below.");
        let errorFields = Object.values(errorMap).slice(0, 3).join("\n");
        showToast("Please check errors before saving." + errorFields, "error");
      } else {
        console.error("Failed to save customer:", err);
        setError(err.message || "Failed to save customer");
        showToast("Failed to save customer", "error");
      }
    } finally {
      setLoading(false);
      setProceedAnyway(false);
    }
  };

  // Handle token association with existing customer
  const handleAssociateToken = async () => {
    if (!customerToken || !formData.externalId) {
      return;
    }

    try {
      setLoading(true);

      await callApi(
        `${API_CONFIG.BASE_URL
        }${API_CONFIG.ENDPOINTS.CUSTOMER_LINK_BY_TOKEN.replace(
          "{token}",
          customerToken
        )}`,
        {
          method: "PUT",
          data: { customerExternalId: formData.externalId },
        }
      );

      showToast("Customer successfully associated with token");

      // Remove token from URL after successful association
      const currentPath = window.location.pathname;
      navigate(currentPath, { replace: true });
      setCustomerToken(null);

    } catch (err: any) {
      console.error("Failed to associate token:", err);
      setError(err.message || "Failed to associate token");
      showToast("Failed to associate token", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    const errors = formData.phones.map((phone) => {
      if (!phone.number) return undefined;
      const parsed = parsePhoneNumberFromString(phone.number, "GB");
      return parsed && parsed.isValid() ? undefined : "Invalid phone number";
    });
    setPhoneValidationErrors(errors);
    if (errors.some((e) => !!e)) {
      setError("Please correct invalid phone numbers.");
      showToast("Please check errors before saving", "error");
      return;
    }

    if (allNull) {
      setShowCommPrefDialog(true);
      return;
    }

    const hasPotentialDuplicate = duplicateMatches.some(
      (match) => match.score >= duplicateCustomerMatchingThreshold
    );
    if (hasPotentialDuplicate && !proceedAnyway) {
      setShowDuplicateWarningDialog(true);
      return;
    }

    saveCustomer();
  };

  // Next of Kin handlers
  // Keep track of the next temporary ID to use for new caregivers
  const [nextTempId] = useState(() => -1);
  const getNextTempId = () => {
    // Find the lowest (most negative) temporary ID currently in use
    const lowestId =
      formData.careGivers?.reduce(
        (lowest, cg) =>
          cg.relationshipId < 0 && cg.relationshipId < lowest
            ? cg.relationshipId
            : lowest,
        nextTempId
      ) ?? nextTempId;
    // Return one less than the lowest to ensure uniqueness
    return lowestId - 1;
  };

  const handleAddCareGiver = (
    newCareGiver: Omit<CareGiver, "relationshipId">
  ) => {
    setFormData((prev) => ({
      ...prev,
      careGivers: [
        ...(prev.careGivers || []),
        {
          ...newCareGiver,
          relationshipId: getNextTempId(), // Use a unique negative ID
        },
      ],
    }));
  };

  const handleUpdateCareGiver = (updatedCareGiver: CareGiver) => {
    setFormData((prev) => ({
      ...prev,
      careGivers:
        prev.careGivers?.map((cg) =>
          cg.relationshipId === updatedCareGiver.relationshipId
            ? updatedCareGiver
            : cg
        ) || [],
    }));
  };

  const handleDeleteCareGiver = (caregiverId: string) => {
    setFormData((prev) => ({
      ...prev,
      careGivers:
        prev.careGivers?.filter(
          (cg) => cg.relationshipId.toString() !== caregiverId
        ) || [],
    }));
  };

  // Delete handler
  const handleDelete = async () => {
    if (!id || isNew) return;
    try {
      setLoading(true);
      setError(null);
      setValidationErrors({});

      await callApi(getApiUrl(API_CONFIG.ENDPOINTS.CUSTOMERS, id), {
        method: "DELETE",
      });
      navigate(NAVIGATION_CONFIG.ROUTES.CUSTOMERS);
    } catch (err: any) {
      if (err.response?.status === 400 && err.response?.data?.errors) {
        const errorMap: ValidationErrors = {};
        err.response.data.errors.forEach(
          (error: { field: string; error: string }) => {
            const fieldName =
              error.field.charAt(0).toLowerCase() + error.field.slice(1);
            errorMap[fieldName] = error.error;
          }
        );
        setValidationErrors(errorMap);
        setError("Cannot delete customer due to validation errors.");
      } else {
        console.error("Failed to delete customer:", err);
        setError(err.message || "Failed to delete customer");
      }
    } finally {
      setLoading(false);
      setDeleteDialogOpen(false);
    }
  };

  // Dialog handlers
  const handleCommPrefDialogSkip = async () => {
    setShowCommPrefDialog(false);
    if (duplicateMatches?.some((match) => match.score >= duplicateCustomerMatchingThreshold)) {
      setShowDuplicateWarningDialog(true);
    } else {
      await saveCustomer();
    }
  };

  const handleDuplicateWarningContinue = () => {
    setShowDuplicateWarningDialog(false);
    setConfirmedNotDuplicate(false);
    setProceedAnyway(true);
    saveCustomer();
  };

  // Loading state

  // Loading state
  if (loading && !isNew) {
    return (
      <Box sx={{ p: 3 }}>
        <Skeleton variant="text" width="30%" height={40} sx={{ mb: 2 }} />
        <Paper sx={{ p: 3 }} elevation={5}>
          <Box display="grid" gridTemplateColumns="repeat(12, 1fr)" gap={3}>
            {[...Array(8)].map((_, index) => (
              <Box key={index} gridColumn={{ xs: "span 12", sm: "span 6" }}>
                <Skeleton variant="text" />
              </Box>
            ))}
          </Box>
        </Paper>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        p: 0,
        pl: 1,
        height: "calc(100vh - 48px)",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        transition: "margin 0.3s",
        marginRight:
          showDuplicates &&
            duplicateDisplayMode === "drawer" &&
            !drawerMinimized
            ? `${drawerWidth}px`
            : 0,
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexGrow: 1,
          flexDirection: "column",
          overflow: "auto",
          minHeight: 0,
          padding: spacing.mui.lg,
        }}
      >
        <CustomerHeader isNew={isNew} onBack={handleBack} />

        <DuplicateToggleControls
          showDuplicates={showDuplicates}
          isNew={isNew}
          duplicateMatches={duplicateMatches}
          onShowDuplicatesChange={setShowDuplicates}
          onShowDuplicatesOnSideChange={(value) => {
            setDuplicateDisplayMode(value ? "drawer" : "panel");
            setDrawerMinimized(false);
          }}
        />

        <DuplicateDrawer
          open={
            showDuplicates &&
            //duplicateMatches.length > 0 && //show drawer even if no matches to allow user to expand it and see search progress
            duplicateDisplayMode === "drawer" &&
            !drawerMinimized
          }
          duplicateMatches={duplicateMatches}
          checkingDuplicates={checkingDuplicates}
          drawerWidth={drawerWidth}
          drawerMinimized={drawerMinimized}
          customerToken={customerToken}
          threshold={duplicateCustomerMatchingThreshold}
          onMinimize={() => setDrawerMinimized(true)}
          onRestore={() => setDrawerMinimized(false)}
          onSelectCustomer={handleSelectCustomer}
        />

        <CustomerStatusBar
          formData={formData}
          validationErrors={validationErrors}
          isNew={isNew}
          deceasedInputTempValue={deceasedInputTempValue}
          onInputChange={handleInputChange}
          onDeceasedDateChange={setDeceasedInputTempValue}
          onDeceasedDateAccept={handleDeceasedDateAccept}
          onDeceasedDateClose={() => {
            if (
              deceasedInputTempValue instanceof Date &&
              isValid(deceasedInputTempValue)
            ) {
              handleDeceasedDateAccept(deceasedInputTempValue);
            }
          }}
          onSystemSelect={handleOpenDesktopApp}
          onCheckSyncStatus={handleCheckSyncStatus}
          onForceSync={handleForceSync}
          showSharedSpace={showSharedSpace}
          onShowSharedSpaceChange={handleSharedSpaceToggleChange}
        />

        {error && (
          <Typography color="error" sx={{ mb: 2 }}>
            {error}
          </Typography>
        )}

        <Box display="flex" gap={3}>
          <Box flex={1} minWidth={0}>
            <CustomerDetailsForm
              formData={formData}
              validationErrors={validationErrors}
              dobInputTempValue={dobInputTempValue}
              careHomes={careHomes}
              loadingCareHomes={loadingCareHomes}
              hasNoEmailChecked={hasNoEmailChecked}
              hasNoPhoneChecked={hasNoPhoneChecked}
              phoneValidationErrors={phoneValidationErrors}
              disabled={formData.isDeceased}
              onInputChange={handleInputChange}
              onDobChange={setDobInputTempValue}
              onDobAccept={handleDobAccept}
              onDobClose={() => {
                if (
                  dobInputTempValue instanceof Date &&
                  isValid(dobInputTempValue)
                ) {
                  handleDobAccept(dobInputTempValue);
                }
              }}
              onCareHomeChange={handleCareHomeChange}
              onCareHomeInputChange={handleCareHomeInputChange}
              onHasNoEmailCheckedChange={handleHasNoEmailCheckedChanged}
              onPhonesChange={(phones) =>
                setFormData((prev) => ({ ...prev, phones }))
              }
              onPhoneValidationErrorsChange={setPhoneValidationErrors}
              onHasNoPhoneCheckedChange={setHasNoPhoneChecked}
            />
          </Box>

          <Box
            flex={1}
            minWidth={0}
            display="flex"
            flexDirection="column"
            gap={3}
          >
            <Box flex={1}>
              {showSharedSpace ? (
                <SharedSpacePanel
                  addresses={formData.addresses}
                  validationErrors={validationErrors}
                  disabled={isAddressPanelDisabled}
                  onAddCareHome={handleAddCareHome}
                  onEditAddress={handleEditAddress}
                  onRemoveAddress={handleRemoveAddress}
                  onUpdateAddress={handleUpdateAddress}
                  onCareHomeClick={handleCareHomeClick}
                />
              ) : (
                <AddressesSection
                  addresses={formData.addresses}
                  validationErrors={validationErrors}
                  disabled={isAddressPanelDisabled}
                  onAddAddress={handleAddAddress}
                  onEditAddress={handleEditAddress}
                  onRemoveAddress={handleRemoveAddress}
                  onUpdateAddress={handleUpdateAddress}
                  onCareHomeClick={handleCareHomeClick}
                  onConvertToSharedSpace={handleConvertToSharedSpace}
                />
              )}
            </Box>
            <Box flex={1}>
              <NextOfKinSection
                careGivers={formData.careGivers || []}
                onAddCareGiver={handleAddCareGiver}
                onUpdateCareGiver={handleUpdateCareGiver}
                onDeleteCareGiver={handleDeleteCareGiver}
                validationErrors={validationErrors}
                disabled={formData.isDeceased}
              />
            </Box>
          </Box>
        </Box>

        <CommunicationPreferences
          communicationPreference={formData.communicationPreference}
          validationErrors={validationErrors}
          isDeceased={formData.isDeceased}
          onInputChange={handleInputChange}
          onReasonChange={handleCommunicationReasonChange}
        />

        {showDuplicates &&
          duplicateMatches.length > 0 &&
          duplicateDisplayMode === "panel" && (
            <DuplicatePanel
              duplicateMatches={duplicateMatches}
              checkingDuplicates={checkingDuplicates}
              customerToken={customerToken}
              threshold={duplicateCustomerMatchingThreshold}
              onSelectCustomer={handleSelectCustomer}
            />
          )}

      </Box>

      <ActionButtons
        isNew={isNew}
        loading={loading || checkingDuplicates}
        formData={formData}
        hasMatch={hasMatch}
        hasNoEmailChecked={hasNoEmailChecked}
        hasNoPhoneChecked={hasNoPhoneChecked}
        allNull={allNull}
        someNull={someNull}
        customerToken={customerToken}
        onDelete={() => setDeleteDialogOpen(true)}
        onSubmit={handleSubmit}
        onAssociateToken={handleAssociateToken}
      />

      {/* Dialogs */}
      <CommunicationPreferencesDialog
        open={showCommPrefDialog}
        onClose={() => setShowCommPrefDialog(false)}
        onSkip={handleCommPrefDialogSkip}
      />

      <DuplicateWarningDialog
        open={showDuplicateWarningDialog}
        confirmedNotDuplicate={confirmedNotDuplicate}
        onClose={() => {
          setShowDuplicateWarningDialog(false);
          setConfirmedNotDuplicate(false);
        }}
        onContinue={handleDuplicateWarningContinue}
        onConfirmedChange={setConfirmedNotDuplicate}
      />

      <NoEmailConfirmationDialog
        open={showNoEmailDialog}
        onClose={handleNoEmailDialogClose}
        onContinue={handleNoEmailDialogContinue}
      />

      <MarkAsDeceasedDialog
        open={showMarkAsDeceasedWarningDialog}
        onClose={() => setShowMarkedAsDeceasedDialog(false)}
        onContinue={handleContinueSetMarkedAsDeceased}
      />

      <DeleteCustomerDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onDelete={handleDelete}
      />

      {/* Address Modals */}
      <AddressLookupModal
        isVisible={isAddressLookupModalOpen}
        onClose={() => setIsAddressLookupModalOpen(false)}
        onAddressSelect={handleAddressSelect}
        onAddAddress={handleAddNewAddress}
      />

      <AddressModal
        isVisible={isAddressModalOpen}
        onClose={() => {
          setSelectedAddress(undefined);
          setIsAddressModalOpen(false);
        }}
        address={selectedAddress}
        onSave={handleSaveAddress}
      />

      {/* Care Home Selection Modal */}
      <CareHomeSelectionModal
        open={isCareHomeModalOpen}
        onClose={() => setIsCareHomeModalOpen(false)}
        onSelectCareHome={handleSelectCareHome}
        onSearchCareHomes={handleSearchCareHomes}
      />

      {/* Shared Space Toggle Confirmation Dialog */}
      <Dialog
        open={sharedSpaceToggleDialogOpen}
        onClose={handleCancelSharedSpaceToggleOff}
        aria-labelledby="shared-space-toggle-dialog-title"
        aria-describedby="shared-space-toggle-dialog-description"
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle
          id="shared-space-toggle-dialog-title"
          sx={{
            backgroundColor: 'warning.light',
            color: 'warning.contrastText',
            fontWeight: 'bold'
          }}
        >
          ⚠️ Are you sure you want to proceed?
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <DialogContentText
            id="shared-space-toggle-dialog-description"
            sx={{
              fontSize: '1.1rem',
              lineHeight: 1.6,
              color: 'text.primary'
            }}
          >
            This action will move the customer, their clinics, and all associated data files from the shared address to a new location.
            <br /><br />
            <strong>Please confirm that this is your intended action.</strong>
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 2 }}>
          <Button
            onClick={handleCancelSharedSpaceToggleOff}
            color="primary"
            variant="outlined"
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmSharedSpaceToggleOff}
            color="warning"
            variant="contained"
            autoFocus
            sx={{ fontWeight: 'bold' }}
          >
            Yes, Proceed
          </Button>
        </DialogActions>
      </Dialog>

      {/* Toast Notification */}
      <Snackbar
        open={toastOpen}
        autoHideDuration={6000}
        onClose={() => setToastOpen(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setToastOpen(false)}
          severity={toastSeverity}
          sx={{ width: '100%' }}
        >
          {toastMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
}
